// sidebarTradeTypes.ts
// Converters that normalize various trade sources into the unified MarketTrade type.

import type { MarketTrade } from './useMarketTrades';
import type { TradePolymarket } from '../../../types/types';

/** Tag colors shared between TradeRow and LiveTradesSidebar */
export const TAG_COLORS: Record<string, string> = {
    'High volume': '#BA7517',
    'Multi-market': '#185FA5',
    'Algo signals': '#A32D2D',
    Insider: '#ff5454',
    'Long-term': '#0F6E56',
};

/** Extract tx_hash from meta_data (handles both object and JSON-string forms) */
function extractTxHash(md: unknown): string | null {
    if (!md) return null;
    if (typeof md !== 'object' && typeof md !== 'string') return null;
    const obj = typeof md === 'string' ? (() => { try { return JSON.parse(md); } catch { return null; } })() : md;
    return ((obj as Record<string, unknown>)?.tx_hash as string) ?? null;
}

/** Convert an RTDB TradePolymarket to MarketTrade */
export function rtdbToMarketTrade(t: TradePolymarket): MarketTrade {
    return {
        id: t.transaction_id,
        marketId: t.market_id,
        timestamp: new Date(t.timestamp),
        price: t.price,
        side: t.side.toLowerCase() as 'buy' | 'sell',
        amount: t.base_amount,
        quoteAmount: t.quote_amount,
        account: t.account ?? '',
        outcomeIndex: t.outcome_index,
        source: 'live',
        isMaker: t.is_maker,
        txHash: extractTxHash(t.meta_data),
    };
}

/** Row shape returned by the indexer trades endpoints */
export interface IndexerTradeItem {
    transaction_id: string;
    market_id: string;
    timestamp: string;
    account: string;
    outcome_index: number;
    side: string;
    price: number;
    base_amount: number;
    quote_amount: number;
    is_maker: boolean;
    tx_hash: string | null;
}

/** Convert an indexer trade item to MarketTrade */
export function indexerTradeToMarketTrade(item: IndexerTradeItem): MarketTrade {
    return {
        id: item.transaction_id,
        marketId: item.market_id,
        timestamp: new Date(item.timestamp),
        price: item.price,
        side: item.side.toLowerCase() as 'buy' | 'sell',
        amount: item.base_amount,
        quoteAmount: item.quote_amount,
        account: item.account ?? '',
        outcomeIndex: item.outcome_index,
        source: 'historical',
        isMaker: item.is_maker,
        txHash: item.tx_hash ?? null,
    };
}
